import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, waLink } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Search, Clock, Calendar as CalIcon, User, ArrowRight, TrendingUp, Compass, Briefcase,
  Heart, HeartPulse, Wallet, Orbit, Star, Zap, CalendarClock, Sparkles, Gem,
  BookOpen, GraduationCap, HelpCircle, MessageCircle, Mail,
} from "lucide-react";
import { toast } from "sonner";
import Seo from "../lib/Seo";

/* ---------------------- Category catalog with icons */
const CATEGORY_META = [
  { key: "Vedic Astrology Basics", icon: Compass },
  { key: "Horoscope Analysis", icon: Star },
  { key: "Career Astrology", icon: Briefcase },
  { key: "Marriage & Relationship Astrology", icon: Heart },
  { key: "Health & Medical Astrology", icon: HeartPulse },
  { key: "Finance & Wealth Astrology", icon: Wallet },
  { key: "Planetary Transits", icon: Orbit },
  { key: "Nakshatra Secrets", icon: Sparkles },
  { key: "Mahadasha & Antardasha", icon: Zap },
  { key: "Panchang & Muhurta", icon: CalendarClock },
  { key: "Astrology Remedies", icon: Star },
  { key: "Gemstones & Rudraksha", icon: Gem },
  { key: "Case Studies", icon: BookOpen },
  { key: "Student Learning Resources", icon: GraduationCap },
  { key: "Course Updates", icon: TrendingUp },
  { key: "Frequently Asked Questions", icon: HelpCircle },
];

function CosmicBackdrop() {
  return (
    <div aria-hidden="true" className="absolute inset-0 overflow-hidden pointer-events-none">
      <div className="absolute inset-0 bg-gradient-to-br from-[#0B1F3A] via-[#0B1F3A] to-[#062036]" />
      <div className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full bg-[#0E9F6E]/12 blur-[120px]" />
      <div className="absolute -bottom-32 right-0 w-[520px] h-[520px] rounded-full bg-[#D4AF37]/10 blur-[120px]" />
      {[...Array(38)].map((_, i) => (
        <span key={i} className="star" style={{
          width: `${Math.random() * 2 + 1}px`, height: `${Math.random() * 2 + 1}px`,
          left: `${Math.random() * 100}%`, top: `${Math.random() * 100}%`,
          animationDelay: `${Math.random() * 4}s`,
        }} />
      ))}
      <svg viewBox="0 0 500 500" className="absolute -right-40 top-10 w-[620px] opacity-15 wheel-spin">
        <circle cx="250" cy="250" r="220" fill="none" stroke="#D4AF37" strokeWidth="1.2" strokeDasharray="2 6" />
        <circle cx="250" cy="250" r="180" fill="none" stroke="#D4AF37" strokeOpacity="0.7" />
        <circle cx="250" cy="250" r="140" fill="none" stroke="#10B981" strokeOpacity="0.5" />
      </svg>
    </div>
  );
}

const fmtDate = (iso) => new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" });

/* Article card */
function ArticleCard({ p, size = "default" }) {
  return (
    <Link to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`} className="group rounded-3xl overflow-hidden bg-background border border-foreground/10 hover:border-[#D4AF37]/60 transition-all hover:-translate-y-1 hover:shadow-xl flex flex-col">
      <div className="aspect-[16/10] overflow-hidden">
        <img src={p.cover_image} alt={p.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
      </div>
      <div className={`p-6 flex flex-col flex-1 ${size === "large" ? "md:p-8" : ""}`}>
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <span className="text-[10px] uppercase tracking-[0.24em] px-2.5 py-1 rounded-full bg-[#10B981]/10 text-[#0E9F6E] border border-[#10B981]/30">{p.category}</span>
          <span className="text-[10px] text-foreground/60 flex items-center gap-1"><Clock className="w-3 h-3" />{p.reading_time || 5} min</span>
        </div>
        <h3 className={`font-serif leading-tight mb-2 group-hover:text-[#D4AF37] transition-colors ${size === "large" ? "text-2xl md:text-3xl" : "text-xl"}`}>{p.title}</h3>
        <p className="text-sm text-foreground/70 line-clamp-2 mb-4 flex-1">{p.excerpt}</p>
        <div className="border-t border-foreground/10 pt-4 flex items-center justify-between text-xs text-foreground/60">
          <span className="flex items-center gap-1.5"><User className="w-3 h-3" />{p.author || "Bhupaal Bisht"}</span>
          <span className="flex items-center gap-1.5"><CalIcon className="w-3 h-3" />{fmtDate(p.created_at)}</span>
        </div>
      </div>
    </Link>
  );
}

/* ============================================================ MAIN */
export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [popular, setPopular] = useState([]);
  const [cats, setCats] = useState([]);
  const [tags, setTags] = useState([]);
  const [cat, setCat] = useState("all");
  const [tag, setTag] = useState("all");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("newest");
  const [visible, setVisible] = useState(6);
  const [news, setNews] = useState({ name: "", email: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/blog/categories").then((r) => setCats(r.data.categories));
    api.get("/blog/tags").then((r) => setTags(r.data.tags));
    api.get("/blog/popular?limit=5").then((r) => setPopular(r.data));
  }, []);

  useEffect(() => {
    const params = new URLSearchParams();
    if (cat !== "all") params.set("category", cat);
    if (tag !== "all") params.set("tag", tag);
    if (q) params.set("q", q);
    if (sort) params.set("sort", sort);
    api.get(`/blog?${params}`).then((r) => {
      setPosts(r.data);
      setVisible(6);
    });
  }, [cat, tag, sort]); // q submitted via form

  const onSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (cat !== "all") params.set("category", cat);
    if (tag !== "all") params.set("tag", tag);
    if (q) params.set("q", q);
    if (sort) params.set("sort", sort);
    api.get(`/blog?${params}`).then((r) => { setPosts(r.data); setVisible(6); });
  };

  const featured = useMemo(() => posts.find((p) => p.featured) || posts[0], [posts]);
  const rest = useMemo(() => posts.filter((p) => !featured || p.id !== featured.id), [posts, featured]);
  const visibleRest = rest.slice(0, visible);

  const submitNewsletter = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/newsletter", { email: news.email });
      toast.success("Subscribed. See you in your inbox!");
      setNews({ name: "", email: "" });
    } catch { toast.error("Could not subscribe. Try again."); }
    finally { setBusy(false); }
  };

  const availableCats = CATEGORY_META.filter((c) => cats.includes(c.key) || true);

  return (
    <div data-testid="blog-page" className="min-h-screen">
      <Seo
        title="Vedic Astrology Blog — Knowledge Hub | YourAstroHub"
        description="Authentic Vedic astrology articles on Nakshatras, Panchang, remedies, planetary transits, dashas, career, marriage, health, gemstones and more. Curated by Bhupaal Bisht."
        keywords="Vedic Astrology Blog, Jyotish Blog, Nakshatra Articles, Panchang, Astrology Remedies, Dasha Explained, Bhupaal Bisht"
        path="/blog"
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Blog", path: "/blog" },
        ]}
      />
      {/* HERO -------------------------------------------------------- */}
      <section className="relative overflow-hidden -mt-20 pt-32 md:pt-40 pb-24 md:pb-28 text-[#F5E8D8] bg-[#0B1F3A]">
        <CosmicBackdrop />
        <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-8 text-center">
          <nav aria-label="breadcrumb" className="text-[11px] tracking-[0.22em] uppercase text-[#F5E8D8]/60 mb-5">
            <Link to="/" className="hover:text-[#D4AF37]">Home</Link>
            <span className="mx-2 text-[#D4AF37]">/</span>
            <span className="text-[#D4AF37]">Blog</span>
          </nav>
          <div className="reveal eyebrow flex items-center justify-center gap-3 mb-6">
            <span className="w-8 gold-hairline" /> Astrology Knowledge Hub <span className="w-8 gold-hairline" />
          </div>
          <h1 data-testid="blog-hero-title" className="reveal-2 font-serif text-4xl md:text-6xl lg:text-7xl font-light leading-[1.05]">
            Astrology <span className="gold-text italic">Knowledge Hub</span>
          </h1>
          <p className="reveal-3 mt-6 text-base md:text-lg text-[#F5E8D8]/75 max-w-3xl mx-auto leading-relaxed">
            Explore authentic Vedic Astrology articles, practical remedies, horoscope insights, planetary transits,
            Nakshatra wisdom, Panchang guidance, and educational resources to help you understand yourself and make
            informed life decisions.
          </p>
          <div className="reveal-4 mt-9 flex flex-wrap gap-3 justify-center">
            <a href="#articles" data-testid="hero-browse">
              <Button size="lg" className="rounded-full bg-[#D4AF37] hover:bg-[#AD974F] text-[#0B1F3A] font-semibold px-8 py-6">
                Browse Articles <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </a>
            <Link to="/consultations" data-testid="hero-book">
              <Button size="lg" className="rounded-full bg-[#25D366] hover:bg-[#20b957] text-white font-semibold px-8 py-6">
                Book a Consultation
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* FEATURED ARTICLE -------------------------------------------- */}
      {featured && (
        <section data-testid="featured-article" className="py-16 md:py-24 bg-background">
          <div className="max-w-7xl mx-auto px-6 md:px-8">
            <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
              <div>
                <div className="eyebrow mb-2">Editor&apos;s Pick</div>
                <h2 className="font-serif text-3xl md:text-4xl font-light">Featured article</h2>
              </div>
              <Link to={`/blog/${featured.slug}`} className="text-[#0E9F6E] hover:text-[#D4AF37] inline-flex items-center gap-2 transition-colors">
                Read the full piece <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <Link to={`/blog/${featured.slug}`} data-testid="featured-article-card" className="group grid md:grid-cols-12 gap-6 items-center rounded-3xl overflow-hidden bg-background border border-[#D4AF37]/30 hover:border-[#D4AF37] transition-all">
              <div className="md:col-span-6">
                <div className="aspect-[16/10] md:aspect-auto md:h-full overflow-hidden">
                  <img src={featured.cover_image} alt={featured.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
              </div>
              <div className="md:col-span-6 p-8 md:p-12">
                <div className="flex items-center gap-2 mb-4 flex-wrap">
                  <span className="text-[10px] uppercase tracking-[0.24em] px-2.5 py-1 rounded-full bg-[#D4AF37] text-[#0B1F3A] font-bold">Featured</span>
                  <span className="text-[10px] uppercase tracking-[0.24em] px-2.5 py-1 rounded-full bg-[#10B981]/10 text-[#0E9F6E] border border-[#10B981]/30">{featured.category}</span>
                  <span className="text-[10px] text-foreground/60 flex items-center gap-1"><Clock className="w-3 h-3" />{featured.reading_time || 5} min</span>
                </div>
                <h3 className="font-serif text-3xl md:text-4xl leading-tight mb-4 group-hover:text-[#D4AF37] transition-colors">{featured.title}</h3>
                <p className="text-foreground/75 leading-relaxed mb-6">{featured.excerpt}</p>
                <div className="flex items-center gap-4 text-sm text-foreground/60 mb-6">
                  <span className="flex items-center gap-1.5"><User className="w-3.5 h-3.5" />{featured.author || "Bhupaal Bisht"}</span>
                  <span className="flex items-center gap-1.5"><CalIcon className="w-3.5 h-3.5" />{fmtDate(featured.created_at)}</span>
                </div>
                <Button size="lg" className="rounded-full bg-[#0B1F3A] hover:bg-[#0F284B] text-[#F5E8D8] dark:bg-[#D4AF37] dark:hover:bg-[#AD974F] dark:text-[#0B1F3A] font-semibold">
                  Read More <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </div>
            </Link>
          </div>
        </section>
      )}

      {/* CATEGORIES -------------------------------------------------- */}
      <section data-testid="categories-grid" className="py-16 md:py-20 bg-[#F4EFE6] dark:bg-[#0F284B]">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="text-center max-w-3xl mx-auto mb-10">
            <div className="eyebrow mb-3">Explore by Category</div>
            <h2 className="font-serif text-4xl md:text-5xl font-light">Browse the <span className="gold-text italic">knowledge hub.</span></h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 gap-3">
            {availableCats.map((c) => {
              const active = cat === c.key;
              return (
                <button
                  key={c.key}
                  data-testid={`cat-${c.key.replace(/\s+/g,'-').toLowerCase()}`}
                  onClick={() => { setCat(active ? "all" : c.key); document.getElementById("articles")?.scrollIntoView({ behavior: "smooth" }); }}
                  className={`group flex items-center gap-3 p-4 rounded-2xl border transition-all text-left ${
                    active ? "bg-[#D4AF37] text-[#0B1F3A] border-[#D4AF37]" : "bg-background border-foreground/10 hover:border-[#D4AF37]/60 hover:-translate-y-0.5"
                  }`}
                >
                  <div className={`w-10 h-10 rounded-xl grid place-items-center shrink-0 ${active ? "bg-[#0B1F3A]/10" : "bg-[#D4AF37]/10 border border-[#D4AF37]/30 group-hover:bg-[#10B981]/10 group-hover:border-[#10B981]/40"}`}>
                    <c.icon className={`w-4 h-4 ${active ? "text-[#0B1F3A]" : "text-[#D4AF37] group-hover:text-[#0E9F6E]"}`} />
                  </div>
                  <span className="text-sm font-medium leading-tight">{c.key}</span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* SEARCH & FILTER ------------------------------------------- */}
      <section id="articles" data-testid="articles-section" className="py-16 md:py-20 bg-background">
        <div className="max-w-7xl mx-auto px-6 md:px-8 grid lg:grid-cols-12 gap-10">
          {/* Left: filters + articles */}
          <div className="lg:col-span-8">
            <form onSubmit={onSearch} className="mb-8 flex flex-col md:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-foreground/40" />
                <Input data-testid="blog-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search articles..." className="rounded-full pl-11" />
              </div>
              <Select value={sort} onValueChange={setSort}>
                <SelectTrigger data-testid="sort-select" className="rounded-full md:w-52"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="newest">Newest</SelectItem>
                  <SelectItem value="oldest">Oldest</SelectItem>
                  <SelectItem value="popular">Most Popular</SelectItem>
                </SelectContent>
              </Select>
              <Select value={tag} onValueChange={setTag}>
                <SelectTrigger data-testid="tag-select" className="rounded-full md:w-40"><SelectValue placeholder="All Tags" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Tags</SelectItem>
                  {tags.map((t) => <SelectItem key={t} value={t}>#{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </form>

            {cat !== "all" && (
              <div className="mb-5 flex items-center gap-2 text-sm text-foreground/70">
                Filter: <span className="px-3 py-1 rounded-full bg-[#D4AF37]/15 text-[#AD974F] border border-[#D4AF37]/40">{cat}</span>
                <button onClick={() => setCat("all")} className="text-xs underline">clear</button>
              </div>
            )}

            <div className="grid sm:grid-cols-2 gap-5">
              {visibleRest.map((p) => <ArticleCard key={p.id} p={p} />)}
              {posts.length === 0 && (
                <div className="sm:col-span-2 text-center py-16 text-foreground/60">No articles found. Try a different search.</div>
              )}
            </div>

            {visible < rest.length && (
              <div className="text-center mt-10">
                <Button data-testid="load-more" onClick={() => setVisible((v) => v + 6)} size="lg" variant="outline" className="rounded-full border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10 px-8">
                  Load More Articles
                </Button>
              </div>
            )}
          </div>

          {/* Sidebar: popular + trending */}
          <aside className="lg:col-span-4 space-y-6" data-testid="sidebar">
            <div className="rounded-3xl border border-foreground/10 p-6">
              <div className="eyebrow mb-4 flex items-center gap-2"><TrendingUp className="w-3 h-3" />Most Read</div>
              <ul className="space-y-4">
                {popular.map((p, i) => (
                  <li key={p.id}>
                    <Link to={`/blog/${p.slug}`} data-testid={`popular-${i}`} className="flex gap-3 group">
                      <div className="w-8 shrink-0 text-3xl font-serif text-[#D4AF37]/30 leading-none">{String(i+1).padStart(2,"0")}</div>
                      <div>
                        <div className="font-serif text-sm md:text-base leading-tight group-hover:text-[#D4AF37] transition-colors">{p.title}</div>
                        <div className="text-[10px] text-foreground/60 mt-1 uppercase tracking-[0.2em]">{p.category}</div>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-3xl border border-foreground/10 p-6">
              <div className="eyebrow mb-4">Trending Tags</div>
              <div className="flex flex-wrap gap-2">
                {tags.slice(0, 12).map((t) => (
                  <button key={t} onClick={() => setTag(t)} data-testid={`tag-chip-${t}`}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${tag === t ? "bg-[#0E9F6E] text-white border-[#0E9F6E]" : "border-foreground/15 hover:border-[#0E9F6E] text-foreground/75"}`}>
                    #{t}
                  </button>
                ))}
              </div>
            </div>

            <div className="rounded-3xl border border-[#D4AF37]/30 p-6 bg-[#0B1F3A] text-[#F5E8D8]">
              <div className="eyebrow mb-3">Personal Guidance</div>
              <h4 className="font-serif text-2xl leading-tight mb-3">Need advice for your unique horoscope?</h4>
              <p className="text-sm text-[#F5E8D8]/70 mb-5">Get a one-to-one consultation with Bhupaal ji.</p>
              <Link to="/consultations">
                <Button size="sm" className="w-full rounded-full bg-[#D4AF37] hover:bg-[#AD974F] text-[#0B1F3A] font-semibold">
                  Book Consultation
                </Button>
              </Link>
            </div>
          </aside>
        </div>
      </section>

      {/* NEWSLETTER -------------------------------------------------- */}
      <section data-testid="newsletter-section" className="py-24 bg-[#F4EFE6] dark:bg-[#0F284B]">
        <div className="max-w-4xl mx-auto px-6 md:px-8 text-center">
          <div className="eyebrow mb-3">Newsletter</div>
          <h2 className="font-serif text-3xl md:text-5xl font-light">
            Stay Updated with <span className="gold-text italic">Weekly Astrology Insights</span>
          </h2>
          <p className="text-foreground/70 mt-5 max-w-2xl mx-auto">
            Subscribe to receive astrology articles, planetary updates, remedies, Panchang highlights, and course announcements directly in your inbox.
          </p>
          <form onSubmit={submitNewsletter} className="mt-8 grid sm:grid-cols-[1fr_1.4fr_auto] gap-3 max-w-2xl mx-auto">
            <Input value={news.name} onChange={(e) => setNews({ ...news, name: e.target.value })} placeholder="Your Name" data-testid="news-name" className="rounded-full" />
            <Input type="email" required value={news.email} onChange={(e) => setNews({ ...news, email: e.target.value })} placeholder="you@example.com" data-testid="news-email" className="rounded-full" />
            <Button type="submit" data-testid="news-submit" disabled={busy} size="lg" className="rounded-full bg-[#D4AF37] hover:bg-[#AD974F] text-[#0B1F3A] font-semibold">
              {busy ? "Subscribing..." : "Subscribe Now"}
            </Button>
          </form>
        </div>
      </section>

      {/* CTA --------------------------------------------------------- */}
      <section data-testid="blog-cta" className="pb-24">
        <div className="max-w-6xl mx-auto px-6 md:px-8">
          <div className="relative rounded-3xl overflow-hidden p-10 md:p-16 bg-[#0B1F3A] text-[#F5E8D8] grain text-center">
            <div className="absolute -top-24 -right-24 w-96 h-96 bg-[#10B981]/25 blur-3xl rounded-full" />
            <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-[#D4AF37]/25 blur-3xl rounded-full" />
            <div className="relative">
              <Sparkles className="w-8 h-8 text-[#D4AF37] mx-auto mb-4" />
              <h3 className="font-serif text-4xl md:text-5xl font-light leading-tight max-w-3xl mx-auto">
                Need Personalized <span className="gold-text italic">Guidance?</span>
              </h3>
              <p className="mt-5 text-[#F5E8D8]/75 max-w-2xl mx-auto text-lg leading-relaxed">
                Every horoscope is unique. If you need advice tailored to your birth chart,
                book a one-to-one consultation with Bhupaal Bisht.
              </p>
              <div className="mt-9 flex flex-wrap gap-3 justify-center">
                <Link to="/consultations" data-testid="cta-book">
                  <Button size="lg" className="rounded-full bg-[#D4AF37] hover:bg-[#AD974F] text-[#0B1F3A] font-semibold px-8 py-6">
                    Book Consultation <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
                <Link to="/courses" data-testid="cta-courses">
                  <Button size="lg" className="rounded-full bg-[#0E9F6E] hover:bg-[#047857] text-white font-semibold px-8 py-6">
                    Explore Courses
                  </Button>
                </Link>
                <a href={waLink("Hello, I would like personalised astrology guidance.")} target="_blank" rel="noreferrer">
                  <Button size="lg" variant="outline" className="rounded-full border-[#25D366] text-[#25D366] hover:bg-[#25D366]/10 px-8 py-6">
                    <MessageCircle className="w-4 h-4 mr-2" />WhatsApp
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
